import { useTranslation } from 'next-i18next';
import React, { ReactElement, ReactNode } from 'react';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import {
  FdrPdf,
  FdrProductB2bInformationGroup,
} from '@hotelplan/supergraph-api';
import {
  FdrAdditionalProductInformation,
  FdrInfoSectionWrapper,
  FdrPdfLinks,
  FdrSectionWrapper,
} from './fdr-b2b-additional-info.styles';

interface IFdrB2bAdditionalInfoProps {
  b2bInfoGroups: Array<FdrProductB2bInformationGroup>;
  hotelPdfs?: Array<FdrPdf>;
  roundTripGallery?: ReactNode;
}

export function FdrB2bAdditionalInfo({
  b2bInfoGroups,
  roundTripGallery,
  hotelPdfs,
}: IFdrB2bAdditionalInfoProps): ReactElement {
  const { channelType } = useAuthentication();

  if (channelType !== AuthChannelType.B2B || !b2bInfoGroups?.length)
    return null;

  return (
    <FdrAdditionalProductInformation>
      {b2bInfoGroups.map((group, index) => (
        <div key={`b2b-info-section-${index}`} className="info-section-wrapper">
          {roundTripGallery && index === 0 ? roundTripGallery : null}
          <FdrInfoSection
            b2bInfoGroup={group}
            hotelPdfs={index === 0 ? hotelPdfs : []}
          />
        </div>
      ))}
    </FdrAdditionalProductInformation>
  );
}

interface IFdrInfoSectionProps {
  b2bInfoGroup: FdrProductB2bInformationGroup;
  hotelPdfs?: Array<FdrPdf>;
}

function FdrInfoSection({
  b2bInfoGroup,
  hotelPdfs,
}: IFdrInfoSectionProps): ReactElement {
  const [t] = useTranslation('pdp');

  const { id, name, textInformation, featuresInformation } = b2bInfoGroup || {};

  return (
    <FdrSectionWrapper data-id={id}>
      {name ? (
        <FdrInfoSectionWrapper>
          <span className="section-name">{name}</span>
        </FdrInfoSectionWrapper>
      ) : null}
      {featuresInformation?.map(group => (
        <FdrInfoSectionWrapper key={group.name}>
          <span className="info-pair-title">{group.name}</span>
          <ul>
            {group.features.map(feature => {
              if (feature.enabled) {
                return <li>{feature.name}</li>;
              }
            })}
          </ul>
        </FdrInfoSectionWrapper>
      ))}
      {textInformation?.map(infoText => {
        const { title, text } = infoText;

        return (
          <FdrInfoSectionWrapper key={title}>
            <span className="info-pair-title">{title}</span>
            {text ? <span dangerouslySetInnerHTML={{ __html: text }} /> : null}
          </FdrInfoSectionWrapper>
        );
      })}
      {hotelPdfs?.length ? (
        <FdrPdfLinks>
          <span className="print-pdf-title">{t('pdp:printPdf.title')}</span>
          {hotelPdfs.map((hotelPdf, index) => {
            return (
              <FdrRoute key={index} className="pdf-link" link={hotelPdf.link}>
                <BsIcon name="download-pdf" />
                {hotelPdf.caption}
              </FdrRoute>
            );
          })}
        </FdrPdfLinks>
      ) : null}
    </FdrSectionWrapper>
  );
}
